/**
 * Catálogo do domínio `projetos` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/projetos.ts`, mais a fatia de `PROJETO` que
 * `cliente.ts` concede ao aprovador.
 *
 * `LEGADO.editor_projetista` aponta para dois papéis (`projetos.escrita` e
 * `projetos.upload`), pelo mesmo motivo de posvendas: nível não implica
 * capacidade (§4.4). Somados, dão o `["read", "readInterno", "create",
 * "update", "upload"]` de `projetos.ts:14`.
 */
import type { NomePapel, Papel } from "../catalogo/vocabulario";

export const PAPEIS_PROJETOS = {
  /**
   * O `adicionar_cliente` em `PROJETO_FABRICA` é concessão cruzada de domínio, e
   * está aqui porque `projetos.ts:10` a dá junto do `manage` em `PROJETO`.
   * Mantida como está: mover para `projetos_fabrica.admin` mudaria quem a tem.
   */
  "projetos.admin": {
    dominio: "projetos",
    nivel: "admin",
    concessoes: [
      { acoes: ["manage"], recurso: "PROJETO" },
      { acoes: ["adicionar_cliente"], recurso: "PROJETO_FABRICA" },
    ],
  },
  "projetos.escrita": {
    dominio: "projetos",
    nivel: "escrita",
    concessoes: [{ acoes: ["read", "readInterno", "create", "update"], recurso: "PROJETO" }],
  },
  "projetos.upload": {
    dominio: "projetos",
    nivel: "upload",
    concessoes: [{ acoes: ["upload"], recurso: "PROJETO" }],
  },
  /** `projetos.ts:12` concede a `projetista` e a `site_manager`. */
  "projetos.leitura": {
    dominio: "projetos",
    nivel: "leitura",
    concessoes: [{ acoes: ["read", "readInterno"], recurso: "PROJETO" }],
  },
  /**
   * Aprovação de projeto pelo cliente (`cliente.ts:19-27`), condicionada ao estado
   * de `Situacao`. Depende de `CAMPO_ESTADO.PROJETO === "Situacao"`.
   *
   * **Um estado, e não quatro (T56, AD-031 D4b).** `cliente.ts:19,25` concedia em
   * `["PAP", "APC", "AP", "PR"]`. "Liberado para aprovação" é `PAP`, e a tradução
   * está no código: `DashboardGeral.vue:154` mapeia
   * `PAP: "EMITIDO PARA APROVAÇÃO DO CLIENTE"`. O cliente deixa de reaprovar o que
   * já aprovou (`AP`, `APC`) e de aprovar o que voltou para revisão (`PR`).
   *
   * **É restrição, não ampliação**, e alinha o ramo cliente com o ramo Gali, que
   * já usa `["PAP"]` em `projetos_fabrica.ts:23`. Muda tela de usuário externo —
   * vai na lista de avisos à área.
   *
   * O `read` vem junto porque o arquivo de origem o concede na mesma chamada.
   */
  "projetos.aprovacao": {
    dominio: "projetos",
    nivel: "aprovacao",
    concessoes: [
      {
        acoes: ["update", "update_status", "read"],
        recurso: "PROJETO",
        estados: ["PAP"],
      },
    ],
  },
  /**
   * A leitura do cliente sobre projeto: `read` e `comentario`, **sem
   * `readInterno`** (T56, AD-031 D4a).
   *
   * É o fechamento da divergência 2 do handoff. `GRUPOS.CLIENTE_LEITOR` e
   * `GRUPOS.CLIENTE_APROVADOR` carregavam `projetos.leitura`, que dá `read` **e**
   * `readInterno` — a leitura interna do projetista Gali (`projetos.ts:12`). Um
   * grupo de cliente carregando a leitura interna entrega dado interno de projeto
   * a usuário externo, e era exatamente o que estava acontecendo na declaração.
   *
   * O `comentario` vem de `cliente.ts:11,15`, na mesma chamada em que o projetista
   * cliente recebe o `read`. É a ação que a T56 acrescentou a `ACOES` — o grep de
   * T43 a tinha perdido.
   */
  "projetos.leitura_externa": {
    dominio: "projetos",
    nivel: "leitura_externa",
    concessoes: [{ acoes: ["read", "comentario"], recurso: "PROJETO" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;
